'use client';

import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@kit/ui/alert';

import { BallMachineCard } from './ball-machine-card';

export interface BallMachineResource {
  id: string;
  name: string;
  bio?: string | null;
  picture_url?: string | null;
  normal_price: number;
  peak_price: number;
  member_price?: number | null;
  /** Phase B: server-resolved price (peak or normal per this slot). */
  original_price?: number | null;
  /** Phase B: server-resolved final price (member + perk baked in). */
  final_price?: number | null;
}

interface BallMachineSelectionProps {
  /** Available ball machines from the time slot data */
  machines: BallMachineResource[];
  /** Whether current slot is peak time */
  isPeakTime: boolean;
  /** Whether user is a venue member (for member pricing) */
  isMember?: boolean;
  selectedMachineId: string;
  onMachineSelect: (machineId: string, machinePrice: number, isPeak: boolean) => void;
}

export function BallMachineSelection({
  machines,
  isPeakTime,
  isMember = false,
  selectedMachineId,
  onMachineSelect,
}: BallMachineSelectionProps) {
  const { t } = useTranslation();

  if (machines.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t('bookings:noBallMachinesAvailable')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {machines.map((machine) => {
          // Phase B: prefer the server-resolved final_price.
          const machinePrice =
            machine.final_price ??
            ((isMember && machine.member_price != null)
              ? machine.member_price
              : (isPeakTime ? machine.peak_price : machine.normal_price));

          return (
            <BallMachineCard
              key={machine.id}
              machine={{
                id: machine.id,
                name: machine.name,
                picture_url: machine.picture_url,
                normal_price: machine.normal_price,
                peak_price: machine.peak_price,
                applicable_price: machinePrice,
              }}
              isSelected={selectedMachineId === machine.id}
              onSelect={() => onMachineSelect(machine.id, machinePrice, isPeakTime)}
              isPeak={isPeakTime}
            />
          );
        })}
    </div>
  );
}
